import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import {Input} from '../ui/input';
import {Label} from '../ui/label';
import {Textarea} from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {AlertTriangle} from 'lucide-react';

const DentalHistorySection = ({formData, setFormData, handleSymptomChange}) => {
  const symptoms = [
    'Tooth pain',
    'Gum bleeding',
    'Sensitivity to hot/cold',
    'Swelling',
    'Bad breath',
    'Loose teeth',
    'Jaw pain',
    'Difficulty chewing',
  ];

  return (
    <Card className="glass-effect border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2 text-blue-400" />
          Dental History & Current Issue
        </CardTitle>
        <CardDescription className="text-gray-300">
          Tell us about your dental history and current concerns
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="lastDentalVisit" className="text-white">
              Last Dental Visit
            </Label>
            <Input
              id="lastDentalVisit"
              type="date"
              value={formData.lastDentalVisit}
              onChange={e =>
                setFormData ({...formData, lastDentalVisit: e.target.value})}
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="painLevel" className="text-white">
              Pain Level (0-10)
            </Label>
            <Select
              value={formData.painLevel}
              onValueChange={value =>
                setFormData ({...formData, painLevel: value})}
            >
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="Select pain level" />
              </SelectTrigger>
              <SelectContent>
                {[...Array (11)].map ((_, i) => (
                  <SelectItem key={i} value={i.toString ()}>
                    {i}
                    {' '}
                    -
                    {' '}
                    {i === 0
                      ? 'No pain'
                      : i <= 3 ? 'Mild' : i <= 6 ? 'Moderate' : 'Severe'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="chiefComplaint" className="text-white">
            Chief Complaint
          </Label>
          <Textarea
            id="chiefComplaint"
            value={formData.chiefComplaint}
            onChange={e =>
              setFormData ({...formData, chiefComplaint: e.target.value})}
            className="bg-gray-800 border-gray-600 text-white"
            placeholder="Describe your main dental concern or reason for visit"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="painDuration" className="text-white">
              Pain Duration
            </Label>
            <Select
              value={formData.painDuration}
              onValueChange={value =>
                setFormData ({...formData, painDuration: value})}
            >
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="How long have you had pain?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hours">A few hours</SelectItem>
                <SelectItem value="days">A few days</SelectItem>
                <SelectItem value="weeks">A few weeks</SelectItem>
                <SelectItem value="months">A few months</SelectItem>
                <SelectItem value="years">More than a year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="painLocation" className="text-white">
              Pain Location
            </Label>
            <Input
              id="painLocation"
              value={formData.painLocation}
              onChange={e =>
                setFormData ({...formData, painLocation: e.target.value})}
              className="bg-gray-800 border-gray-600 text-white"
              placeholder="Which tooth/area hurts?"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-white">
            Current Symptoms (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {symptoms.map (symptom => (
              <label
                key={symptom}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.symptoms.includes (symptom)}
                  onChange={() => handleSymptomChange (symptom)}
                  className="rounded border-gray-600 bg-gray-800 text-blue-500"
                />
                <span className="text-white text-sm">{symptom}</span>
              </label>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DentalHistorySection;
